import { FC, useState, useEffect } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Contract } from "@models/contract.model";
import { CreateContractPayload } from "@services/finance/contracts/commands/cmdCreateContract";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import OrganizationPicker from "@/shared/components/OrganizationPicker/OrganizationPicker";
import { BILLING_MODEL_OPTIONS, PRODUCT_OPTIONS } from "../../types";
import { JourneyService, JourneyOption } from "@services/journeys/journeyService";
import "./contractListSize.css";

/*
    The vertical's journey, previously settable only in Django admin (MPN-1573).

    Defaulted by NAME rather than id because ids differ per environment, and the ticket
    names the default rather than pointing at a row. A rename in Journey Steps therefore
    drops the default rather than silently picking the wrong vertical, which is the safer
    way to be wrong: a blank field gets questioned, a confidently wrong one does not.
*/
const DEFAULT_JOURNEY_NAME = "College Degree";

interface Props {
  show: boolean;
  onClose: () => void;
  /** Called with a payload appropriate for create or update depending on whether `contract` is set. */
  onSave: (payload: CreateContractPayload | UpdateContractPayload) => void;
  contract: Contract | null;
  /** The client (org owning this view). Required when creating. */
  clientOrgId: number;
  /**
   * Forces the service provider instead of letting the user pick one — used by
   * the engagement Contract item, where the provider is the engagement's.
   */
  lockedServiceProviderOrgId?: number | null;
  isSaving: boolean;
}

interface FormState {
  name: string;
  service_provider_org_id: string;
  billing_method: string;
  po: string;
  contract_url: string;
  contract_start_date: string;
  contract_end_date: string;
  products: string[];
  revenue_cap: string;
  max_list_size: string;
  sized_by: string;
  list_type: string;
  is_active: boolean;
  journey_id: string;
}

const BILLING_VALUES = new Set(BILLING_MODEL_OPTIONS.map((m) => m.value));

const EMPTY: FormState = {
  name: "",
  service_provider_org_id: "",
  billing_method: "PFR",
  po: "",
  contract_url: "",
  contract_start_date: "",
  contract_end_date: "",
  products: [],
  revenue_cap: "",
  max_list_size: "",
  sized_by: "",
  list_type: "",
  is_active: true,
  journey_id: "",
};

const ContractFormModal: FC<Props> = ({
  show,
  onClose,
  onSave,
  contract,
  clientOrgId,
  lockedServiceProviderOrgId = null,
  isSaving,
}) => {
  const isEdit = contract !== null;
  const isProviderLocked = !isEdit && lockedServiceProviderOrgId != null;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [journeys, setJourneys] = useState<JourneyOption[]>([]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    new JourneyService()
      .getJourneys()
      .then((list) => {
        if (!cancelled) setJourneys(list);
      })
      // A picker that cannot load its options is not worth failing the whole form over:
      // every other field still saves, and the journey keeps whatever it already had.
      .catch(() => {
        if (!cancelled) setJourneys([]);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  /*
      Default only on create, and only once the list has arrived. An existing contract keeps
      what Django gave it — the ticket is explicit that the new UI must not reset or default
      those, which is the one way this change could quietly repoint a live client's vertical.
  */
  useEffect(() => {
    if (!show || isEdit || journeys.length === 0) return;
    setForm((p) => {
      if (p.journey_id) return p;
      const fallback = journeys.find(
        (j) => j.name.trim().toLowerCase() === DEFAULT_JOURNEY_NAME.toLowerCase(),
      );
      return fallback ? { ...p, journey_id: String(fallback.id) } : p;
    });
  }, [show, isEdit, journeys]);

  useEffect(() => {
    if (!show) return;
    if (contract) {
      setForm({
        name: contract.name ?? "",
        service_provider_org_id: String(contract.service_provider_org_id),
        billing_method: BILLING_VALUES.has(contract.billing_method ?? "")
          ? (contract.billing_method ?? "")
          : "PFR",
        po: contract.po ?? "",
        contract_url: contract.contract_url ?? "",
        contract_start_date: contract.contract_start_date ?? "",
        contract_end_date: contract.contract_end_date ?? "",
        products: [...(contract.products ?? [])],
        revenue_cap: contract.revenue_cap != null ? String(contract.revenue_cap) : "",
        max_list_size:
          contract.max_list_size != null ? String(contract.max_list_size) : "",
        sized_by: contract.sized_by ?? "",
        list_type: contract.list_type ?? "",
        is_active: contract.is_active,
        journey_id: contract.journey_id != null ? String(contract.journey_id) : "",
      });
    } else {
      setForm({
        ...EMPTY,
        service_provider_org_id:
          lockedServiceProviderOrgId != null ? String(lockedServiceProviderOrgId) : "",
      });
    }
  }, [contract, show, lockedServiceProviderOrgId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const toggleProduct = (value: string, checked: boolean) =>
    setForm((p) => ({
      ...p,
      products: checked
        ? [...p.products, value]
        : p.products.filter((x) => x !== value),
    }));

  const handleSave = () => {
    const common = {
      name: form.name.trim() || null,
      billing_method: form.billing_method || null,
      po: form.po || null,
      contract_url: form.contract_url || null,
      contract_start_date: form.contract_start_date || null,
      contract_end_date: form.contract_end_date || null,
      products: form.products,
      revenue_cap: form.revenue_cap ? parseFloat(form.revenue_cap) : null,
      // Empty stays null rather than 0: a contract nobody has sized is unknown, and 0
      // would read as a ceiling of none, putting every campaign under it over cap.
      max_list_size: form.max_list_size ? parseInt(form.max_list_size, 10) : null,
      sized_by: form.sized_by || null,
      list_type: form.list_type || null,
      is_active: form.is_active,
      journey_id: form.journey_id ? Number(form.journey_id) : null,
    };

    if (isEdit) {
      onSave(common satisfies UpdateContractPayload);
    } else {
      onSave({
        ...common,
        client_org_id: clientOrgId,
        service_provider_org_id: Number(form.service_provider_org_id),
      } satisfies CreateContractPayload);
    }
  };

  const selectedProviderId = form.service_provider_org_id
    ? Number(form.service_provider_org_id)
    : null;
  const canSubmit = isEdit || !!form.service_provider_org_id;

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isEdit ? "Edit Contract" : "Create Contract"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Contract Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="e.g. Fall 2026 Re-Enrollment"
                />
                <Form.Text className="text-muted">
                  Shown wherever this contract appears. Falls back to the contract number.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Service Provider <span className="text-danger">*</span>
                </Form.Label>
                <OrganizationPicker
                  value={selectedProviderId}
                  onChange={(id) =>
                    update("service_provider_org_id", id != null ? String(id) : "")
                  }
                  placeholder="Select provider..."
                  disabled={isEdit || isProviderLocked}
                  pinFirst="Motimatic"
                />
                {isEdit && (
                  <Form.Text className="text-muted">
                    Provider cannot be changed after creation
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.contract_start_date}
                  onChange={(e) => update("contract_start_date", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Billing Method</Form.Label>
            <Form.Select
              value={form.billing_method}
              onChange={(e) => update("billing_method", e.target.value)}
            >
              {BILLING_MODEL_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Revenue-recognition model used for reporting.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.contract_end_date}
                  onChange={(e) => update("contract_end_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Revenue Cap</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.revenue_cap}
                  onChange={(e) => update("revenue_cap", e.target.value)}
                  placeholder="0.00"
                />
                <Form.Text className="text-muted">
                  Optional ceiling on revenue this contract may accrue.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="listSizeSection mb-3">
            <div className="listSizeSection__badge">New — list size</div>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Contract list size</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={form.max_list_size}
                onChange={(e) => update("max_list_size", e.target.value)}
                placeholder="0"
              />
              <Form.Text className="text-muted">
                Total list size allowed for campaigns under this Contract. Reference only
                &mdash; not enforced automatically.
              </Form.Text>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Sized by</Form.Label>
                  <Form.Select
                    value={form.sized_by}
                    onChange={(e) => update("sized_by", e.target.value)}
                  >
                    <option value="">Not set</option>
                    <option value="CONTRACT">Contract</option>
                    <option value="CAMPAIGN">Campaign</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Contract: the sum of list size for campaigns under this Contract
                    cannot exceed this. Campaign: the list size for each campaign cannot
                    exceed this.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">List type</Form.Label>
                  <Form.Select
                    value={form.list_type}
                    onChange={(e) => update("list_type", e.target.value)}
                  >
                    <option value="">Not set</option>
                    <option value="STATIC">Static</option>
                    <option value="DYNAMIC">Dynamic</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    A static list is sent once; a dynamic one keeps arriving.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Journey</Form.Label>
                <Form.Select
                  value={form.journey_id}
                  onChange={(e) => update("journey_id", e.target.value)}
                >
                  <option value="">None</option>
                  {journeys.map((j) => (
                    <option key={j.id} value={String(j.id)}>
                      {j.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  The vertical whose Journey steps this contract's engagements pull. Defined in
                  Journey Steps.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Purchase Order</Form.Label>
                <Form.Control
                  type="text"
                  value={form.po}
                  onChange={(e) => update("po", e.target.value)}
                  placeholder="PO-..."
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Contract URL</Form.Label>
                <Form.Control
                  type="url"
                  value={form.contract_url}
                  onChange={(e) => update("contract_url", e.target.value)}
                  placeholder="https://..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-block">Products</Form.Label>
            {PRODUCT_OPTIONS.map((p) => (
              <Form.Check
                key={p.value}
                inline
                type="checkbox"
                id={`contract-product-${p.value}`}
                label={p.label}
                checked={form.products.includes(p.value)}
                onChange={(e) => toggleProduct(p.value, e.target.checked)}
              />
            ))}
          </Form.Group>

          <hr className="my-3" />

          <Form.Check
            type="switch"
            id="contract-active-switch"
            label="Active"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !canSubmit}>
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2" />
              {isEdit ? "Save Changes" : "Create Contract"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContractFormModal;
